import { SetOptional } from 'type-fest'
import { baseSchema, Update } from './base'
import { generate } from './generate'
import { z } from 'zod'
import { hashJson } from '@basehub/utils/hash'
import { workflowPayloadSchema } from './block/workflow'

/* -------------------------------------------------------------------------------------------------
 * Notification
 * -----------------------------------------------------------------------------------------------*/

const notificationBase = baseSchema.extend({
  id: z.string(),
  userId: z.string(),
  read: z.boolean(),
  createdAt: z.string(),
  authorId: z.string(),
  authorUsername: z.string(),
})

const notification = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('repo-thread-comment'),
    content: z.string(),
    commentType: z.enum(['root', 'reply']),
    href: z.string(),
    branchId: z.string(),
    branchName: z.string(),
    repoName: z.string().optional(),
    rootId: z.string().optional(),
    commentId: z.string().optional(),
  }),
  z.object({
    type: z.literal('repo-cr-resolution'),
    resolution: z.enum(['merged', 'archived']),
    href: z.string(),
    branchId: z.string(),
    branchName: z.string(),
    branchDescription: z.string().nullish(),
    baseBranchId: z.string().nullish(),
    baseBranchName: z.string().nullish(),
    repoName: z.string().optional(),
  }),
  z.object({
    type: z.literal('workflow-action'),
    payload: workflowPayloadSchema.and(
      z.object({ count: z.number().optional() })
    ),
    branchId: z.string(),
    branchName: z.string(),
    repoName: z.string().optional(),
    rootId: z.string().optional(),
    href: z.string(),
  }),
])

export const notificationSchema = notificationBase.and(notification)
export const genericNotification = notificationBase
  .omit({ userId: true, id: true })
  .and(notification)

export type Notification = z.infer<typeof notificationSchema>
export type GenericNotification = z.infer<typeof genericNotification>
export type NotificationUpdate = Update<Notification>

export const notificationBaseOps = generate(
  'notification',
  notificationSchema.parse
)

/* -------------------------------------------------------------------------------------------------
 * Notification Subscription
 * -----------------------------------------------------------------------------------------------*/

const notificationSubscriptionSchema = baseSchema
  .extend({
    id: z.string(), // hash(<event-type>/<event-id>)
    userIds: z.string().array(),
  })
  .and(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal('thread'),
        rootId: z.string(),
      }),
      z.object({
        type: z.literal('branch-events'),
        branchId: z.string(),
      }),
      z.object({
        type: z.literal('workflow-block'),
        blockId: z.string(),
      }),
    ])
  )

export type NotificationSubscription = z.infer<
  typeof notificationSubscriptionSchema
>
export type NotificationSubscriptionUpdate = Update<NotificationSubscription>

export const notificationSubscriptionBaseOps = generate(
  'notification-subscription',
  notificationSubscriptionSchema.parse
)

export function getNotificationSubscriptionId({
  id: _,
  userIds: _u,
  ...rest
}: SetOptional<NotificationSubscription, 'id' | 'userIds'>) {
  return hashJson(rest)
}
